import { Button } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";

import { Datepicker } from "../../../Datepicker";
import { DropdownOption, DropDownV2 } from "../../../DropDownV2";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { SearchInput } from "../../../SearchInput";
import { CLIENT_ANF_TABLE_FILTER_REPORT_START_DATE } from "../consts";
import { useClientApprovedNotFundedContext } from "../providers/ApprovedNotFundedProvider";
import { ClientANFTableFilterSubProps } from "./types";

export const MobileFilters = ({
  handleDateRangePicker,
  handleExportButton,
  handleSearchInput,
  handleRebateAccountsDropdown,
  translations: { translatedContent, language },
  onDateTooltipHover,
}: ClientANFTableFilterSubProps) => {
  const isMobile = useIsMobile();

  const translatedLabels = useMemo(
    () => ({
      selectAccountLabel: translatedContent.dropDownLabels?.selectLabel ?? "",
      allItemsSelectedLabel:
        translatedContent.dropDownLabels?.allItemsSelectedLabel ?? "",
      itemsSelectedLabel:
        translatedContent.dropDownLabels?.itemsSelectedLabel ?? "",
      clearSelectionLabel:
        translatedContent.dropDownLabels?.clearSelectionLabel ?? "",
      selectAllLabel: translatedContent.dropDownLabels?.selectAllLabel ?? "",
      applyButtonLabel:
        translatedContent.dropDownLabels?.applyButtonLabel ?? "",
    }),
    [
      translatedContent.dropDownLabels?.allItemsSelectedLabel,
      translatedContent.dropDownLabels?.applyButtonLabel,
      translatedContent.dropDownLabels?.clearSelectionLabel,
      translatedContent.dropDownLabels?.itemsSelectedLabel,
      translatedContent.dropDownLabels?.selectAllLabel,
      translatedContent.dropDownLabels?.selectLabel,
    ],
  );

  const {
    state: {
      searchQuery,
      dateRange,
      selected: { rebateAccountIDs: selectedRebateAccountIDs },
    },
    options: { rebateAccountIDs },
  } = useClientApprovedNotFundedContext();

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedRebateAccountIDs as string[]) ?? []).map((id) => ({
      label:
        rebateAccountIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateAccountIDs, selectedRebateAccountIDs]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleRebateAccountsDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  return (
    <div data-testid="mobile-filters" className="mt-4 flex flex-col gap-4">
      <div>
        <Datepicker
          value={{
            startDate: dateRange.from,
            endDate: dateRange.to,
          }}
          inputId="dateRangeFilter"
          onChange={handleDateRangePicker}
          configs={{
            shortcuts: {
              today: translatedContent.today,
              yesterday: translatedContent.yesterday,
              past: (period) =>
                translatedContent.past ? translatedContent.past(period) : "",
              currentMonth: translatedContent.currentMonth,
              pastMonth: translatedContent.pastMonth,
            },
            footer: {
              cancel: translatedContent.cancel,
              apply: translatedContent.apply,
            },
          }}
          i18n={language}
          label={
            translatedContent.approvedNotFundedTab?.filters?.approvalDate ||
            translatedContent.filters?.dates
          }
          allowClear={false}
          minDate={new Date(CLIENT_ANF_TABLE_FILTER_REPORT_START_DATE)}
          tooltip={
            translatedContent.filters
              ?.approvedNotFundedDatePickerDisclaimerTooltip
          }
          isMobile={isMobile}
          onTooltipHover={onDateTooltipHover}
        />
      </div>
      <div>
        <DropDownV2
          id="mobileSearchDropdown"
          options={rebateAccountIDs}
          selected={selectedRebateAccounts}
          label={translatedContent.filters?.rebateAccounts}
          isMobile={isMobile}
          dropDownLabels={translatedLabels}
          handleApply={handleDropdownChange}
        />
      </div>
      <div>
        <SearchInput
          label={translatedContent.searchLabel}
          value={search}
          onChange={handleSearchChange}
          placeholder={translatedContent?.filters?.searchNoPII}
        />
      </div>
      <div className="flex h-[72px] flex-row items-end justify-end">
        <Button
          as="button"
          type="button"
          size="ps-btn-md"
          variant="ps-btn-secondary"
          onClick={handleExportButton}
        >
          {translatedContent.exportCSVText}
        </Button>
      </div>
    </div>
  );
};
